/* eslint-disable react-hooks/rules-of-hooks */
import { HTTP_STATUS } from "@api/constant";
import ArticleCard from "@blocks/Card/ArticleCard";
import { BANNER_CONFIG, BannerEvents } from "@config/BannerConfigConstants";
import { LOCALHOST } from "@config/MetaTagsConstants";
import MainBlock from "@container/MainBlock";
import MainContainer, { AdsSettings } from "@container/MainContainer";
import NdSideBlock from "@container/SideBlock/NdSideBlock";
import SideBlock from "@container/SideBlock/SideBlock";
import WrapperBlock from "@container/WrapperBlock";
import { RenderImu1AdInfiniteScroll } from "@elements/Advertisement/RenderImu1AdInfiniteScroll";
import { SpecialHeader } from "@header/SpecialHeader";
import fetchTMApi from "@helper/fetchTMApi";
import { GADataProps, getGTMData } from "@helper/getGtmData";
import { displayAfter, hideAfter } from "@helper/utils";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import {
  CreateLoadMoreHandler,
  type CreateLoadMoreHandlerProps,
} from "@hooks/useLoadMore/CreateLoadMoreHandler";
import LoadMore from "@hooks/useLoadMore/LoadMore";
import useSetFirstRenderedArticleIds from "@hooks/useLoadMore/SetFirstRenderedArticleIds";
import { useMobWebView } from "@hooks/useMobWebView";
import { Route } from "@route-types/Keyword/+types/Keyword";
import ErrorPage from "@routes/ErrorPage";
import fetchKeywordData from "@routes/Keyword/Keyword.server";
import BudgetLegacy from "@routes/Keyword/Variants/BudgetLegacy";
import BudgetVariant from "@routes/Keyword/Variants/BudgetVariant";
import Deepavali from "@routes/Keyword/Variants/Deepavali";
import NdpVariant from "@routes/Keyword/Variants/NdpVariant";
import NotFound from "@routes/NotFound";
import type {
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { createMetaTags, MetaTagsProps } from "@sphtech/web2-seo-schema";
import { type ProcessedArticleData } from "@transformer/useOsResponse";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { MoreArticlesRequestData } from "@typings/MoreArticlesApi";
import { KeywordPayloadResponseType } from "@typings/PagePayloadType";
import { Fragment } from "react";
import { useState } from "react";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

const PAGE_SIZE = 5;
const INITIAL_ARTICLE_COUNT = 5;

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    KeywordPayloadResponseType,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags(metaData);
  }
}

export default function KeywordPage() {
  const dataLoaderResponse: TRouteWithoutRedirect<
    KeywordPayloadResponseType,
    string
  > = useLoaderData();

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  const keywordArticles = dataLoaderResponse.payload.data;
  const tagAlias = dataLoaderResponse.payload.tagAlias;
  const liveBlog = dataLoaderResponse.payload.liveBlog;
  const ndpVariant = dataLoaderResponse.payload.ndpVariant;
  const recordedVideo = dataLoaderResponse.payload.recordedVideo;

  const budget2026PdfLink = dataLoaderResponse.payload.budget2026PdfLink;
  const budget2026Variant = dataLoaderResponse.payload.budget2026Variant;

  const adsSettings: AdsSettings = useGlobalAdSetting();
  const mobwebviewBool = useMobWebView();

  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>(),
  );
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);

  const showLoadMore: boolean = keywordArticles.length == INITIAL_ARTICLE_COUNT;
  useSetFirstRenderedArticleIds(keywordArticles, setRenderedArticleIds);

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length == 0 ? keywordArticles.at(-1) : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn: tagAlias,
      sort: lastLoadedArticle?.sort,
    };

    const fetchMoreTagsStoriesData = async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[] | []> => {
      const queryParams = new URLSearchParams({
        tagAlias: requestData.filterArticlesBasedOn as string,
        sort: Array.isArray(requestData.sort) ? requestData.sort.join(",") : "",
        size: "5",
      }).toString();
      const response: ProcessedArticleData[] = await fetchTMApi(
        `more-tags-stories?${queryParams}`,
        "GET",
      );

      return response;
    };
    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction: fetchMoreTagsStoriesData,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: PAGE_SIZE,
      pageNumber,
    };
    const response = await CreateLoadMoreHandler(createLoadMoreProps);
    return response;
  };
  const secondaryTagAlias = tagAlias === "deepavali-2025" ? "deepavali" : ""; // Added to handle Deepavali and Deepavali 2025 tag names
  let tagName = "";
  for (const tag of keywordArticles[0].tags || []) {
    if (
      tag.alias?.toLowerCase().includes(tagAlias.toLowerCase()) ||
      (secondaryTagAlias != "" &&
        tag.alias?.toLowerCase().includes(secondaryTagAlias.toLowerCase()))
    ) {
      if (tag.name !== undefined) {
        tagName = tag.name;
        break;
      }
    }
  }
  let showBreadcrumbs = true;
  let showTitle = true;
  const specialTags = [
    "தேசிய தினம் 2024",
    "தேசிய தினம் 2025",
    "பட்ஜெட் 2025",
    "தீபாவளி 2025",
    "தீபாவளி",
    "பட்ஜெட் 2026",
  ];
  if (specialTags.includes(tagName)) {
    showBreadcrumbs = false;
    showTitle = false;
  }
  const pageAdTargetValue = PageAdTargetingTypeEnum.TAG;
  const sectionName = "keywords";
  const sectionNameMap: Record<string, string> = {
    "தேசிய தினம் 2025": BannerEvents.NATIONAL_DAY_2025,
    "பட்ஜெட் 2026": "budget-2026",
  };
  const overriddenSectionName = sectionNameMap[tagName] ?? sectionName;

  const getSpecialVariant = () => {
    const commonProps = {
      tagAlias,
      tagName,
      sectionName,
      keywordArticles,
      showBreadcrumbs,
      showTitle,
      pageAdTargetValue,
    };

    switch (tagName) {
      case "பட்ஜெட் 2025":
        return <BudgetLegacy {...commonProps} />;
      case "பட்ஜெட் 2026":
        return (
          <BudgetVariant
            {...commonProps}
            recordedVideo={recordedVideo}
            liveBlog={liveBlog}
            articlePlacement={
              budget2026Variant === "article" ? "main" : "sidebar"
            }
            articleSectionTitle={`${tagName} செய்திகள்`}
            budget2026PdfLink={budget2026PdfLink}
          />
        );
      case BANNER_CONFIG[BannerEvents.NATIONAL_DAY_2025].title:
        return (
          <NdpVariant
            {...commonProps}
            liveBlog={liveBlog}
            ndpVariant={ndpVariant}
          />
        );
      case "தீபாவளி 2025":
      case "தீபாவளி":
        return <Deepavali {...commonProps} recordedVideo={recordedVideo} />;
      default:
        return null;
    }
  };

  const SideBlockComponent =
    tagName === "தேசிய தினம் 2024" ? NdSideBlock : SideBlock;

  return (
    <MainContainer
      sectionName={overriddenSectionName}
      pageAdTargetValue={pageAdTargetValue}
      mobwebview={mobwebviewBool}
      specialBackground={tagName === "பட்ஜெட் 2026"}
      suppliedAdsSettings={adsSettings}
    >
      {specialTags.includes(tagName) && <SpecialHeader title={tagName} />}

      {specialTags.includes(tagName) ? (
        getSpecialVariant()
      ) : (
        <WrapperBlock className="flex flex-col lg:flex-row justify-between">
          <MainBlock
            sectionName={tagName}
            showBreadcrumbs={showBreadcrumbs}
            showTitle={showTitle}
          >
            {[...keywordArticles, ...loadMoreData].map((section, index) => (
              <Fragment key={section.id}>
                <ArticleCard
                  displayWidth={260}
                  displayHeight={192}
                  elements={{ hasDate: true }}
                  {...section}
                  overrideStyle={{
                    cardWrapper:
                      (displayAfter(index + 1, PAGE_SIZE)
                        ? "border-none"
                        : "flex-col lg:flex-row") +
                      (hideAfter(index, PAGE_SIZE)
                        ? " lg:flex-row flex-row-reverse"
                        : ""),
                    titleStyle: hideAfter(index, PAGE_SIZE)
                      ? "font-semibold text-2sm text-gray-100 hover:text-gray-100 lg:text-2lg lg:text-red-100 lg:hover:text-red-200"
                      : "text-md lg:text-2lg",
                    paragraphStyle: hideAfter(index, PAGE_SIZE)
                      ? "hidden lg:block"
                      : "",
                    imageWrapperStyle: hideAfter(index, PAGE_SIZE)
                      ? "w-31.5 h-23 lg:h-auto"
                      : "",
                    imageStyle: hideAfter(index, PAGE_SIZE)
                      ? "h-23 lg:h-auto"
                      : "",
                  }}
                />
                {displayAfter(index + 1, PAGE_SIZE) && (
                  <>
                    {RenderImu1AdInfiniteScroll(
                      (index + 1) / PAGE_SIZE,
                      sectionName,
                      pageAdTargetValue,
                    )}
                  </>
                )}
              </Fragment>
            ))}
            {showLoadMore ? (
              <div className="flex w-full justify-center mb-xl">
                <LoadMore
                  rootClassName="my-4 w-full"
                  onLoadMore={handleLoadMore}
                  hasMore={hasMoreStories}
                  loadText="Load More"
                  mobileInfiniteScroll={true}
                  pageNumber={pageNumber}
                  sectionName={sectionName}
                />
              </div>
            ) : null}
          </MainBlock>
          <SideBlockComponent
            sectionName={sectionName}
            className="w-full lg:block"
            pageAdTargetValue={pageAdTargetValue}
            hideAdForMobile={true}
          />
        </WrapperBlock>
      )}
    </MainContainer>
  );
}

export async function loader({ request }: Route.LoaderArgs) {
  const url = new URL(request.url, LOCALHOST);
  const tagName = url.pathname.replace("/keywords/", "");

  const data = (await fetchKeywordData(tagName)) as TRouteWithRedirect<
    KeywordPayloadResponseType,
    string
  >;
  if (data.statusCode !== HTTP_STATUS.SUCCESS) return { ...data };
  const gaData = data.payload.gaData;

  return {
    ...data,
    gtm: getGTMData(gaData as GADataProps),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<KeywordPayloadResponseType, string>
  >;
}
